/**
 * Kullanıcıya görünen biçimler (CLAUDE.md "Dil ve isimlendirme").
 * Tarih GG.AA.YYYY · saat SS:dd · para ₺1.240,50 · plaka 34 ABC 123
 * Para hesabı burada YAPILMAZ; yalnızca backend'in döndürdüğü string/sayı biçimlenir.
 */

import { t } from './i18n';

const TR_TZ = 'Europe/Istanbul';

export function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: TR_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: TR_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatDateTime(d: Date | string): string {
  return `${formatDate(d)} ${formatTime(d)}`;
}

/**
 * Para biçimi. Girdi backend'den gelen ondalık string'dir ("1240.50").
 * Sayı da kabul edilir ama hesap yapılmaz, yalnızca gösterilir.
 */
export function formatMoney(value: string | number, withSymbol = true): string {
  const n = typeof value === 'string' ? Number(value) : value;
  const s = Number.isFinite(n)
    ? new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : '0,00';
  // Termal fişte ₺ işareti yazıcının karakter setine göre bozulabilir; makbuz onu istemez
  // (prototip §3.18: "kalem ↔ tutar (₺ işaretsiz)").
  return withSymbol ? `₺${s}` : s;
}

/**
 * Cari bakiye — Türk muhasebe geleneği: bakiye İŞARETLE değil B/A ekiyle gösterilir.
 * Sunucunun döndürdüğü Decimal STRING'ini alır; panelde ekstresle aynı dili konuşur:
 * pozitif (müşteri borçlu) → "₺136,50 B", negatif (müşterinin alacağı) → "₺25,00 A",
 * sıfır → "₺0,00" (ek yok).
 *
 * PARA HESABI YAPILMAZ: yön yalnızca string'in başındaki '-' işaretinden okunur, mutlak
 * değer '-' atılmış string'dir ve `formatMoney` ile biçimlenir. Böylece çıktıda
 * "-₺" / "₺-" biçimi hiç oluşmaz.
 */
export function formatBalance(value: string): string {
  // Sunucu her zaman string döner; eksik/bozuk payload paneli patlatmamalı (boş → "₺0,00").
  const raw = typeof value === 'string' ? value.trim() : '';
  const negative = raw.startsWith('-');
  const abs = negative ? raw.slice(1) : raw;
  const money = formatMoney(abs === '' ? '0' : abs);
  // Sıfır bakiyede yön eki basılmaz. Karar BİÇİMLENMİŞ çıktıdan verilir: "0.001" de "₺0,00"
  // yazılır ve ekstrenin `balanceText`'i gibi ek almaz.
  if (money === formatMoney('0')) return money;
  return `${money} ${negative ? t('statement.balanceSuffix.credit') : t('statement.balanceSuffix.debit')}`;
}

/**
 * Sayı biçimi — tr-TR: ondalık ayracı virgül, binlik ayracı nokta.
 *
 * `formatNumber(1234.5, 1)` → `"1.234,5"` · `formatNumber(1234)` → `"1.234"`.
 *
 * Bu YALNIZ GÖSTERİM; para hesabı yapılmaz.
 */
export function formatNumber(value: number, fractionDigits = 0): string {
  if (!Number.isFinite(value)) return '0';
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Kilometre: `formatKm(1234.5)` → `"1.234,5 km"` (bir ondalık). */
export function formatKm(value: number): string {
  return `${formatNumber(value, 1)} km`;
}

/**
 * Yüzde. Girdi ORAN'dır (0..1): `formatPercent(0.125)` → `"%12,5"`.
 *
 * Türkçe yazımda yüzde işareti ÖNDE gelir. Oran çarpımı yalnızca gösterim içindir;
 * iş kuralları (kâr marjı vb.) backend'de hesaplanır.
 */
export function formatPercent(ratio: number, fractionDigits = 1): string {
  if (!Number.isFinite(ratio)) return `%${formatNumber(0, fractionDigits)}`;
  return `%${formatNumber(ratio * 100, fractionDigits)}`;
}

/**
 * Kısa para: `16234` → `"₺16,2 bin"` · `2_500_000` → `"₺2,5 mn"` · `950` → `"₺950"`.
 *
 * Eşikler: 1000 altı tam sayı, 1.000.000 altı "bin", üstü "mn".
 * Ekler `tr.ts`'ten gelir (`format.thousand`, `format.million`) — kullanıcıya görünen
 * metin koda gömülmez.
 */
export function formatShortMoney(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '₺0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs < 1000) return `${sign}₺${formatNumber(abs, 0)}`;
  // 999.950 ve üstü "bin"de 1.000,0'a yuvarlanır; o noktadan itibaren "mn" yazılır.
  if (abs < 999_950) {
    return `${sign}₺${formatNumber(abs / 1000, 1)} ${t('format.thousand')}`;
  }
  return `${sign}₺${formatNumber(abs / 1_000_000, 1)} ${t('format.million')}`;
}

/** "34abc123" / "34 abc 123" → "34 ABC 123". Biçim tanınmazsa büyük harfe çevirip döner. */
export function formatPlate(raw: string): string {
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  const m = /^(\d{2})([A-Z]{1,3})(\d{2,5})$/.exec(compact);
  if (!m) return raw.trim().toUpperCase();
  return `${m[1]} ${m[2]} ${m[3]}`;
}

/** Türkçe büyük harf: "i" → "İ", "ı" → "I". `toUpperCase()` bunu yanlış yapar. */
export function upperTr(s: string): string {
  return s.toLocaleUpperCase('tr-TR');
}

export function lowerTr(s: string): string {
  return s.toLocaleLowerCase('tr-TR');
}

/**
 * Kullanıcının yazdığı Türkçe para metnini backend'in beklediği biçime çevirir.
 *
 * `"1.250,75"` → `"1250.75"` · `"1250"` → `"1250"` · `"12,5"` → `"12.5"`.
 * Geçersizse `null` döner — çağıran formu KAPATMAZ, hata gösterir.
 *
 * Neden burada: panelde rakam dışı her karakteri silmek (`replace(/[^\d]/g, '')`) ondalık
 * ayracını da siliyordu ve `1.250,75` deftere **125.075,00** olarak yazılıyordu. Defter satırı
 * geri alınamaz (kural 1); üç ayrı ters kayıt ve iptal edilemeyen bir makbuz bırakır.
 */
export function parseMoneyInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  // Türkçe biçim: nokta binlik ayracı, virgül ondalık. Yalnız bu iki ayraca izin verilir.
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d{1,10}([.,]\d{1,2})?$/.test(trimmed)) return null;
  const hasComma = trimmed.includes(',');
  const normalized = hasComma
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : // Virgül yoksa nokta ondalık mı binlik mi belirsiz: "1.250" binlik, "12.50" ondalıktır.
      /^\d{1,3}(\.\d{3})+$/.test(trimmed)
      ? trimmed.replace(/\./g, '')
      : trimmed;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return normalized;
}

/**
 * Sunucudan gelen ondalık string ile tam sayı adedi çarpıp sonucu yine "0.00" biçimli
 * string döner — kural 4: float çarpımı kullanılmaz.
 *
 * Kuruş cinsinden `BigInt` aritmetiği yapar (`0.10 * 3 = 0.30000000000000004` tuzağından
 * kaçar). Sonuç her zaman iki ondalıklı, nokta ayraçlıdır. Geçersiz girdi → `'0.00'`.
 *
 * Bu YALNIZ ÖNİZLEME içindir (mobil teslimat ekranı depozito önizlemesi gibi). Gerçek
 * para sunucuda `Prisma.Decimal` ile yazılır; buradaki sonuç KULLANICIYA GÖSTERİLİR,
 * istekle birlikte gönderilmez.
 */
export function multiplyMoney(amount: string, qty: number): string {
  if (!Number.isFinite(qty) || qty < 0) return '0.00';
  const trimmed = amount.trim();
  if (trimmed === '') return '0.00';
  // Ondalık kısmı en fazla 2 hane olmalı (sunucu `toFixed(2)` döner). Daha fazlası geldiyse
  // girdi bozuktur — `0.00` ile güvenli tarafta kal.
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return '0.00';
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const parts = abs.split('.');
  const whole = parts[0] ?? '0';
  const frac = parts[1] ?? '';
  const cents = BigInt(whole) * 100n + BigInt((frac + '00').slice(0, 2));
  const total = cents * BigInt(Math.trunc(qty));
  const sign = negative || total < 0n ? '-' : '';
  const absTotal = total < 0n ? -total : total;
  const wholeOut = (absTotal / 100n).toString();
  const fracOut = (absTotal % 100n).toString().padStart(2, '0');
  return `${sign}${wholeOut}.${fracOut}`;
}
